import {
  fetchUtils,
  HttpError,
  Identifier,
  RaRecord,
  UpdateManyParams,
  UpdateManyResult,
} from 'react-admin';
import {
  createReactAdminValidationError,
  getJsonApiErrorBody,
  getJsonApiErrorMessage,
  getJsonApiErrorStatus,
  JsonApiResource,
} from './JsonApiTypes';
import type { AuthProvider } from './AuthProvider';
import batch, { BatchOperation } from './batch';
import { isJsonApiResourceLink } from './utils';

export default function updateMany(
  apiUrl: string,
  httpClient = fetchUtils.fetchJson,
  canAccess: typeof AuthProvider.canAccess = async () => true,
) {
  const executeBatch = batch(apiUrl, httpClient, canAccess);

  return async <T extends RaRecord<Identifier>>(
    resource: string,
    params: UpdateManyParams<T>,
  ): Promise<UpdateManyResult<T & JsonApiResource>> => {
    try {
      const ops: BatchOperation[] = [];
      for (const id of params.ids) {
        if (
          params.data.attributes &&
          Object.values(params.data.attributes).some((v) => v !== undefined)
        ) {
          const attributes = Object.fromEntries(
            Object.entries(params.data.attributes).filter(([, v]) => v !== undefined),
          );
          ops.push({
            op: 'update',
            data: { id: String(id), type: resource, attributes },
          });
        }

        if (params.data.relationships) {
          for (const [key, relationshipData] of Object.entries(params.data.relationships)) {
            if (isJsonApiResourceLink(relationshipData)) {
              ops.push({
                op: 'update',
                ref: { id: String(id), type: resource, relationship: key },
                data: relationshipData.data,
              });
            }
          }
        }
      }

      if (ops.length > 0) {
        await executeBatch(ops);
      }

      return { data: params.ids as (T & JsonApiResource)['id'][] };
    } catch (error) {
      const validationErrors = createReactAdminValidationError(error as HttpError);
      throw new HttpError(
        getJsonApiErrorMessage(error as HttpError, 'Error updating records'),
        getJsonApiErrorStatus(error as HttpError),
        {
          ...validationErrors,
          apiResponse: getJsonApiErrorBody(error),
        },
      );
    }
  };
}
